import React, { useState, useCallback } from 'react';
import { message, Modal, Progress } from 'antd';
import axios from 'axios';
import dayjs from 'dayjs';
import { generateLabel } from './OrderListItem';
import { useWorker } from '../../hooks/useWorker';

const BatchOperations = ({ 
  selectedOrderIds, 
  orders, 
  onOrdersUpdate,
  onChangeCustomer
}) => {
  const [downloadModalVisible, setDownloadModalVisible] = useState(false);
  const [downloadProgress, setDownloadProgress] = useState(0);
  const [isGenerating, setIsGenerating] = useState(false);
  
  // Use a worker for label generation to avoid blocking the main thread
  const { runWorker } = useWorker();

  // Process labels in chunks to improve performance
  const processInChunks = async (items, processFn, chunkSize = 5) => {
    const results = [];
    const chunks = [];
    
    // Split items into chunks
    for (let i = 0; i < items.length; i += chunkSize) {
      chunks.push(items.slice(i, i + chunkSize));
    }
    
    let processedCount = 0;
    const totalItems = items.length;
    
    // Process each chunk
    for (const chunk of chunks) {
      // Process items in a chunk concurrently
      const chunkResults = await Promise.all(chunk.map(processFn));
      results.push(...chunkResults);
      
      // Update progress
      processedCount += chunk.length;
      setDownloadProgress(Math.round((processedCount / totalItems) * 100));
    }
    
    return results;
  };

  const handleBatchPrint = useCallback(async () => {
    if (selectedOrderIds.length === 0) {
      message.warning('请选择要打印的订单');
      return;
    }

    message.loading({ content: '准备打印...', key: 'batchPrintLoading' });

    try {
      // Make a single API call for all orders
      await axios.post('/api/orders/batch_print/', {
        order_ids: selectedOrderIds
      });

      // Record history for each order
      await Promise.all(
        selectedOrderIds.map(orderId =>
          axios.post(`/api/orders/${orderId}/history/`, {
            action: 'print'
          })
        )
      );

      const selectedOrders = orders.filter(order => selectedOrderIds.includes(order.id));
      
      // Generate labels in chunks to avoid blocking the UI
      const labelCanvases = await processInChunks(
        selectedOrders,
        async (order) => {
          try {
            // Web Worker implementation is not complete, use direct generation
            const canvas = await generateLabel(order);
            return canvas;
          } catch (error) {
            console.error('Label generation error:', error);
            return null;
          }
        }
      );
      
      const validCanvases = labelCanvases.filter(canvas => canvas !== null);

      if (validCanvases.length === 0) {
        message.error({ content: '面单生成失败', key: 'batchPrintLoading' });
        return;
      }

      // Convert canvases to data URLs
      const dataUrls = validCanvases.map(canvas => 
        canvas.toDataURL('image/jpeg', 0.95)
      );

      // Create a function to print a single label
      const printSingleLabel = (dataUrl, index, total) => {
        return new Promise((resolve) => {
          const printWindow = window.open('', '_blank');
          if (!printWindow) {
            message.error({ content: '请允许弹出窗口以进行打印', key: 'batchPrintLoading' });
            resolve(false);
            return;
          }
          
          // Write the HTML for a single label
          printWindow.document.write(`
            <!DOCTYPE html>
            <html>
              <head>
                <style>
                  @page { size: 100mm 150mm; margin: 0; }
                  body { margin: 0; padding: 0; }
                  .label-container { 
                    width: 100mm; 
                    height: 150mm;
                  }
                  img { 
                    width: 100%; 
                    height: 100%; 
                    display: block; 
                  }
                </style>
              </head>
              <body>
                <div class="label-container">
                  <img src="${dataUrl}" />
                </div>
                <script>
                  window.onload = () => {
                    setTimeout(() => {
                      window.print();
                      setTimeout(() => {
                        window.close();
                        window.opener.postMessage('printed', '*');
                      }, 100);
                    }, 100);
                  };
                </script>
              </body>
            </html>
          `);
          printWindow.document.close();
          
          // Listen for the message that printing is done
          window.addEventListener('message', function handler(event) {
            if (event.data === 'printed') {
              window.removeEventListener('message', handler);
              resolve(true);
            }
          });
          
          // Fallback in case the message event doesn't fire
          setTimeout(() => resolve(true), 2000);
        });
      };
      
      // Print labels one by one
      message.loading({ content: '正在打印...', key: 'batchPrintLoading' });
      
      // Use a for loop with await to print one label at a time
      for (let i = 0; i < dataUrls.length; i++) {
        await printSingleLabel(dataUrls[i], i, dataUrls.length);
        // Small delay between print jobs
        await new Promise(resolve => setTimeout(resolve, 500));
      }
      
      message.success({ content: '打印请求已发送', key: 'batchPrintLoading' });
    } catch (error) {
      console.error('Batch print error:', error);
      message.error({ content: '批量打印失败', key: 'batchPrintLoading' });
    }
  }, [selectedOrderIds, orders, runWorker]);

  const handleDownloadPdf = useCallback(() => {
    if (selectedOrderIds.length === 0) {
      message.warning('请选择要下载的订单');
      return;
    }
    setDownloadModalVisible(true);
  }, [selectedOrderIds]);

  const handleConfirmDownload = useCallback(async () => {
    setIsGenerating(true);
    setDownloadProgress(0);

    try {
      // Record history for each order
      await Promise.all(
        selectedOrderIds.map(orderId =>
          axios.post(`/api/orders/${orderId}/history/`, {
            action: 'save'
          })
        )
      );
      
      const selectedOrders = orders.filter(order => selectedOrderIds.includes(order.id));
      
      // Generate labels in chunks
      const labelResults = await processInChunks(
        selectedOrders,
        async (order) => {
          try {
            // Web Worker implementation is not complete, use direct generation
            const canvas = await generateLabel(order);
            
            return canvas ? {
              canvas,
              orderNumber: order.order_number
            } : null;
          } catch (error) {
            console.error('Label generation error:', error);
            return null;
          }
        }
      );
      
      const labelCanvases = labelResults.filter(result => result !== null);

      if (labelCanvases.length === 0) {
        throw new Error('No labels were generated successfully');
      }

      // Dynamically import JSZip only when needed
      const JSZip = (await import('jszip')).default;
      const zip = new JSZip();

      // Add files to zip
      labelCanvases.forEach(({ canvas, orderNumber }) => {
        const dataUrl = canvas.toDataURL('image/jpeg', 0.95);
        const base64Data = dataUrl.split(',')[1];
        zip.file(`${orderNumber}.jpg`, base64Data, { base64: true });
      });

      // Generate and download zip
      const content = await zip.generateAsync({ type: 'blob' });
      const url = window.URL.createObjectURL(content);
      const link = document.createElement('a');
      link.href = url;
      link.download = `订单面单_${dayjs().format('YYYY-MM-DD_HH-mm')}.zip`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);

      message.success('面单下载成功');
    } catch (error) {
      console.error('Download error:', error);
      message.error('面单下载失败');
    } finally {
      setIsGenerating(false);
      setDownloadModalVisible(false);
      setDownloadProgress(0);
    }
  }, [selectedOrderIds, orders, runWorker]);

  const showDeleteConfirm = useCallback(() => {
    const count = selectedOrderIds.length;
    const token = localStorage.getItem('token');
    let isStaffOrBoss = false;
    
    try {
      const payload = JSON.parse(atob(token.split('.')[1]));
      isStaffOrBoss = payload.is_staff || payload.is_superuser;
    } catch (e) {
      console.error('Error decoding token:', e);
    }

    Modal.confirm({
      title: '确认删除',
      content: isStaffOrBoss 
        ? `警告：这将永久删除${count}个订单，此操作无法撤销。确定要继续吗？`
        : `确定要删除${count}个订单吗？`,
      okText: '确认',
      okType: 'danger',
      cancelText: '取消',
      onOk: handleBatchDelete
    });
  }, [selectedOrderIds]);

  const handleBatchDelete = useCallback(async () => {
    if (selectedOrderIds.length === 0) {
      message.warning('请选择要删除的订单');
      return;
    }

    try {
      await axios.post('/api/orders/batch_delete/', {
        order_ids: selectedOrderIds
      });
      message.success('批量删除成功');
      onOrdersUpdate();
    } catch (error) {
      console.error('Error deleting orders:', error);
      message.error('删除失败');
    }
  }, [selectedOrderIds, onOrdersUpdate]);

  const handleExport = useCallback(async () => {
    if (selectedOrderIds.length === 0) {
      message.warning('请选择要导出的订单');
      return;
    }

    message.loading({ content: '正在导出...', key: 'exportLoading' });

    try {
      const response = await axios.post('/api/orders/export/', {
        order_ids: selectedOrderIds
      }, {
        responseType: 'blob'
      });

      const blob = new Blob([response.data], { 
        type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' 
      });
      
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `订单导出_${dayjs().format('YYYY-MM-DD_HH-mm')}.xlsx`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);

      message.success({ content: '导出成功', key: 'exportLoading' });
    } catch (error) {
      console.error('Export error:', error);
      message.error({ content: '导出失败', key: 'exportLoading' });
    }
  }, [selectedOrderIds]);

  return {
    handleBatchPrint,
    handleDownloadPdf,
    showDeleteConfirm,
    handleExport,
    downloadModalVisible,
    downloadProgress,
    isGenerating,
    handleConfirmDownload,
    setDownloadModalVisible,
  };
};

export default BatchOperations;
